import { enqueueSnackbar } from 'notistack';
import { fetchAuthSession } from 'aws-amplify/auth';

import { AMPLIFY_API } from '../config-global';
import { nativeInfo, isNativeApp } from './native-bridge';
import { DeviceService } from '../services/DeviceService';
import FirebaseService from '../services/FirebaseService';
import { createDeviceRegistration } from './device-registration';

const key = `heyjob.devices.${encodeURIComponent(import.meta.env.VITE_API_URL)}.${AMPLIFY_API.userPoolId}.${AMPLIFY_API.userPoolWebClientId}`;
let owner = null;
let busy = false;
let paused = false;
let revision = 0;
const supported = () => !isNativeApp() && !!navigator.locks && !!globalThis.crypto?.getRandomValues;
const secret = () => btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32)))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
const registry = createDeviceRegistration({
  onPersistenceFailure: () => enqueueSnackbar('La disattivazione delle notifiche non è stata confermata. Riapri HeyJob con una connessione disponibile per riprovare.', { variant: 'warning' }),
  load: async () => JSON.parse(localStorage.getItem(key) || 'null'),
  save: async state => { localStorage.setItem(key, JSON.stringify(state)); },
  lock: fn => navigator.locks.request(key, fn),
  uuid: () => crypto.randomUUID(), secret,
  register: async (input, signal) => {
    // Revalidate inside the cross-tab lock; a token obtained before logout is stale.
    const { tokens } = await fetchAuthSession();
    if (signal.aborted || !owner || tokens?.idToken?.payload.sub !== owner) throw Object.assign(new Error('Session changed'), { status: 409 });
    return DeviceService.register(input, String(tokens.idToken), signal);
  },
  disassociate: input => DeviceService.disassociate(input),
  metadata: async () => {
    const ua = navigator.userAgent;
    const browser = [[/Edg\//, 'Edge'], [/Firefox\//, 'Firefox'], [/Chrome\//, 'Chrome'], [/Safari\//, 'Safari']].find(([pattern]) => pattern.test(ua))?.[1] || 'Browser';
    const os = [[/Android/, 'Android'], [/iPhone|iPad/, 'iOS'], [/Mac/, 'macOS'], [/Windows/, 'Windows']].find(([pattern]) => pattern.test(ua))?.[1] || 'Linux / altro';
    return { platform: 'web', client: 'browser', device_name: `${browser} su ${os}`, ...await FirebaseService.getPushRegistration() };
  },
});
// Result: 'ok', 'failed' (retry) or 'skipped' (nothing to retry now).
export async function syncBrowserDevice() {
  if (!supported() || busy || paused || !navigator.onLine || document.visibilityState === 'hidden') return 'skipped';
  busy = true;
  const generation = revision;
  try {
    if (owner) {
      const { tokens } = await fetchAuthSession();
      if (!tokens?.idToken || !tokens.accessToken?.payload['cognito:groups']?.includes('Dipendente')) return 'skipped';
      if (generation !== revision || tokens.idToken.payload.sub !== owner) return 'skipped';
      await registry.sync(owner);
    } else await registry.retryPending();
    return 'ok';
  } catch (error) {
    // No raw errors: request details may contain tokens/limited credentials.
    // A stale association was already replaced and retried once by the registry.
    if (error.status === 400 || error.status === 409) { paused = true; return 'skipped'; }
    return 'failed';
  } finally { busy = false; }
}

// Retry only after a failure: owner change, network, visibility and permission
// already trigger a sync. No periodic registration while everything succeeds.
let started = false;
let retryTimer;
let attempt = 0;
let running = false;
let rerun = false;
function retryAfter(result) {
  if (!started || result === 'skipped') return;
  clearTimeout(retryTimer);
  if (result === 'ok') { attempt = 0; return; }
  retryTimer = setTimeout(triggerSync, [5000, 30000, 300000][Math.min(attempt, 2)]);
  attempt += 1;
}
// A trigger during a running sync runs once more afterwards instead of being dropped.
function triggerSync() {
  if (running) { rerun = true; return; }
  running = true;
  syncBrowserDevice().then(result => {
    running = false;
    if (rerun) { rerun = false; triggerSync(); } else retryAfter(result);
  });
}
export async function disconnectBrowserDevice(clearSession = async () => {}) {
  const signingOut = owner;
  revision += 1; owner = null; paused = false;
  if (supported()) await registry.disconnect(clearSession, signingOut);
  else await clearSession();
}
export function setBrowserDeviceOwner(value) {
  if (owner !== value) { revision += 1; registry.cancel(); paused = false; }
  owner = value;
  triggerSync();
}
export function startBrowserDevices() {
  if (!supported()) return () => {};
  started = true;
  attempt = 0;
  const changed = event => {
    if (event.key !== key || !owner) return;
    try {
      const next = JSON.parse(event.newValue || 'null');
      if (next?.current?.owner === owner) return;
      revision += 1; registry.cancel(); paused = true; owner = null;
      window.dispatchEvent(new Event('heyjobDeviceSignedOut'));
    } catch { registry.cancel(); paused = true; }
  };
  window.addEventListener('storage', changed);
  window.addEventListener('online', triggerSync);
  window.addEventListener('heyjobPushPermissionChanged', triggerSync);
  document.addEventListener('visibilitychange', triggerSync);
  triggerSync();
  return () => {
    started = false; clearTimeout(retryTimer); registry.cancel();
    window.removeEventListener('storage', changed); window.removeEventListener('online', triggerSync);
    window.removeEventListener('heyjobPushPermissionChanged', triggerSync); document.removeEventListener('visibilitychange', triggerSync);
  };
}


/** Read only: a WebView must never fall back to a separate browser identity. */
export function getCurrentInstallationId() {
  if (isNativeApp()) return nativeInfo()?.installationId;
  try { return JSON.parse(localStorage.getItem(key) || 'null')?.installation_id; }
  catch { return undefined; }
}
